/* eslint-disable react-refresh/only-export-components -- a compat shim:
   hooks and Link/Navigate components are exported together on purpose. */
/**
 * Router-compat shim — bridges @/compat/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 *
 * TanStack's `to` / `search` / `state` are generic over the app's route
 * tree; this shim only ever holds runtime path strings and plain records.
 * `navigateLoose` and `looseRouteProps` are the two points that cross that
 * boundary, each with a single `as never` that names the gap rather than a
 * cast scattered through every call site.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
  RouterProvider,
  createRootRoute,
  createRouter,
  createMemoryHistory,
} from "@tanstack/react-router";
import {
  useMemo,
  useCallback,
  useState,
  forwardRef,
  type ComponentProps,
  type ReactNode,
} from "react";

/** The subset of TanStack's navigate options a v6 caller can express. */
type LooseNav = (opts: {
  to: string;
  search?: Record<string, string>;
  hash?: string;
  state?: unknown;
  replace?: boolean;
}) => void;

/** Untyped runtime path meets the route-typed API here, and only here. */
function navigateLoose(
  tsNav: ReturnType<typeof tsNavigate>,
  opts: Parameters<LooseNav>[0],
): void {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- a shim has no route id to satisfy the generic `to`/`search`/`state`
  (tsNav as unknown as LooseNav)(opts);
}

/** A component whose props are runtime strings, not route-tree literals. */
type LooseComponent = (props: Record<string, unknown>) => ReactNode;

// The same crossing as `navigateLoose` for the `<Link>` / `<Navigate>` /
// `<RouterProvider>` elements: their prop types are generic over the route
// tree, and a shim has only runtime values to give them.
/* eslint-disable @typescript-eslint/consistent-type-assertions -- a shim cannot supply the route-literal generics these components are typed on */
const LooseLink = TSLink as unknown as LooseComponent;
const LooseNavigate = TSNavigate as unknown as LooseComponent;
const LooseRouterProvider = RouterProvider as unknown as LooseComponent;
/* eslint-enable @typescript-eslint/consistent-type-assertions */

// ---------- shared URL parsing ----------

function parseTo(to: string): {
  pathname: string;
  search?: Record<string, string>;
  hash?: string;
} {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = beforeHash.split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr
      ? Object.fromEntries(new URLSearchParams(searchStr))
      : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        router.history.go(to);
        return;
      }
      const { pathname, search, hash } = parseTo(to);
      navigateLoose(tsNav, {
        to: pathname,
        search,
        hash,
        state: options?.state,
        replace: options?.replace,
      });
    },
    [tsNav, router],
  );
}

// ---------- useLocation ----------

interface CompatLocation {
  pathname: string;
  search: string;
  hash: string;
  /** v6 callers read history state untyped; TanStack types it as a struct. */
  state: unknown;
  key: string;
}

export function useLocation(): CompatLocation {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: loc.state ?? null,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>(): T {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- a shim passes no route id; T is the caller's per-route param shape
  return tsParams({ strict: false } as never) as T;
}

// ---------- useSearchParams (@/compat/router-compat compat) ----------

export function useSearchParams(): [
  URLSearchParams,
  (
    init:
      | URLSearchParams
      | Record<string, string>
      | ((prev: URLSearchParams) => URLSearchParams),
    opts?: { replace?: boolean },
  ) => void,
] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(
    () => new URLSearchParams(loc.searchStr ?? ""),
    [loc.searchStr],
  );
  const setParams = useCallback(
    (
      init:
        | URLSearchParams
        | Record<string, string>
        | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => {
        searchObj[k] = v;
      });
      navigateLoose(nav, {
        to: live.pathname,
        search: searchObj,
        replace: opts?.replace,
      });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <LooseLink
      {...{ ...rest, ref, to: pathname, search, hash, replace, state }}
    >
      {children}
    </LooseLink>
  );
});

// ---------- Navigate ----------

export function Navigate({
  to,
  replace,
  state,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) {
  const { pathname, search, hash } = parseTo(to);
  return <LooseNavigate {...{ to: pathname, search, hash, state, replace }} />;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;

// ---------- MemoryRouter (test/story harness) ----------

// A self-contained router for Storybook decorators and tests: renders its
// children inside a real TanStack router backed by memory history, so
// Link/NavLink/useLocation work without the app's route tree.
export function MemoryRouter({
  children,
  initialEntries = ["/"],
}: {
  children?: ReactNode;
  initialEntries?: string[];
}) {
  const [router] = useState(() => {
    const rootRoute = createRootRoute({ component: () => <>{children}</> });
    return createRouter({
      routeTree: rootRoute,
      history: createMemoryHistory({ initialEntries }),
    });
  });
  return <LooseRouterProvider {...{ router }} />;
}
